import { Link } from "react-router-dom";
import { Cookie, Settings, BarChart3, Target, Shield } from "lucide-react";

const CookiePolicy = () => {
  const lastUpdated = "January 21, 2026";
  return <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black backdrop-blur-md border-b border-white/10">
        
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Cookie className="w-10 h-10 text-purple-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Cookie Policy</h1>
          </div>
          <div className="text-sm text-gray-400">
            <span>Last Updated: {lastUpdated}</span>
          </div>
        </div>

        {/* Introduction */}
        <section className="mb-10">
          <p className="text-lg text-gray-300 leading-relaxed">
            Metro City Trading (Pty) Ltd ("Metro City Groove", "we", "us", or "our") uses cookies and similar 
            tracking technologies on our platform. This Cookie Policy explains what cookies are, how we use them, 
            your choices regarding cookies, and further information about cookies.
          </p>
        </section>

        {/* What Are Cookies */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">What Are Cookies?</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you 
              visit a website. They are widely used to make websites work more efficiently and provide information 
              to the website owners.
            </p>
            <p className="text-gray-400">
              Cookies can be "persistent" (remaining on your device until deleted or expired) or "session" 
              (deleted when you close your browser). They can be set by the website you're visiting ("first-party") 
              or by third parties ("third-party").
            </p>
          </div>
        </section>

        {/* Types of Cookies */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-6">Types of Cookies We Use</h2>
          
          <div className="space-y-6">
            {/* Essential Cookies */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Shield className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Essential Cookies</h3>
                  <span className="text-sm text-green-400">Always Active</span>
                </div>
              </div>
              <p className="text-gray-400 mb-4">
                These cookies are necessary for the website to function and cannot be switched off. They are 
                usually set in response to actions you take, such as setting privacy preferences, logging in, 
                or filling in forms.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left border-b border-white/10">
                    <tr>
                      <th className="py-2 pr-4 text-gray-400">Cookie Name</th>
                      <th className="py-2 pr-4 text-gray-400">Purpose</th>
                      <th className="py-2 text-gray-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-white/5">
                      <td className="py-2 pr-4 font-mono text-xs">sb-access-token</td>
                      <td className="py-2 pr-4">Authentication session</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 pr-4 font-mono text-xs">sb-refresh-token</td>
                      <td className="py-2 pr-4">Session refresh</td>
                      <td className="py-2">7 days</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 pr-4 font-mono text-xs">cookie-consent</td>
                      <td className="py-2 pr-4">Stores your cookie preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-xs">csrf-token</td>
                      <td className="py-2 pr-4">Security protection</td>
                      <td className="py-2">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Settings className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Functional Cookies</h3>
                  <span className="text-sm text-blue-400">Optional</span>
                </div>
              </div>
              <p className="text-gray-400 mb-4">
                These cookies enable enhanced functionality and personalization, such as remembering your 
                preferences and settings. They may be set by us or by third-party providers whose services 
                we use.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left border-b border-white/10">
                    <tr>
                      <th className="py-2 pr-4 text-gray-400">Cookie Name</th>
                      <th className="py-2 pr-4 text-gray-400">Purpose</th>
                      <th className="py-2 text-gray-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-white/5">
                      <td className="py-2 pr-4 font-mono text-xs">theme</td>
                      <td className="py-2 pr-4">Dark/light mode preference</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 pr-4 font-mono text-xs">language</td>
                      <td className="py-2 pr-4">Language preference</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-xs">recent-searches</td>
                      <td className="py-2 pr-4">Recent search history</td>
                      <td className="py-2">30 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Analytics Cookies</h3>
                  <span className="text-sm text-yellow-400">Optional</span>
                </div>
              </div>
              <p className="text-gray-400 mb-4">
                These cookies help us understand how visitors interact with our website by collecting and 
                reporting information anonymously. This helps us improve our Services.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left border-b border-white/10">
                    <tr>
                      <th className="py-2 pr-4 text-gray-400">Cookie Name</th>
                      <th className="py-2 pr-4 text-gray-400">Provider</th>
                      <th className="py-2 pr-4 text-gray-400">Purpose</th>
                      <th className="py-2 text-gray-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-white/5">
                      <td className="py-2 pr-4 font-mono text-xs">_ga</td>
                      <td className="py-2 pr-4">Google Analytics</td>
                      <td className="py-2 pr-4">Visitor identification</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2 pr-4 font-mono text-xs">_gid</td>
                      <td className="py-2 pr-4">Google Analytics</td>
                      <td className="py-2 pr-4">Session tracking</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-xs">_gat</td>
                      <td className="py-2 pr-4">Google Analytics</td>
                      <td className="py-2 pr-4">Request throttling</td>
                      <td className="py-2">1 minute</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Target className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Marketing Cookies</h3>
                  <span className="text-sm text-purple-400">Optional</span>
                </div>
              </div>
              <p className="text-gray-400 mb-4">
                These cookies are used to deliver advertisements more relevant to you and your interests. 
                They may also be used to limit the number of times you see an advertisement and measure 
                the effectiveness of advertising campaigns.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left border-b border-white/10">
                    <tr>
                      <th className="py-2 pr-4 text-gray-400">Cookie Name</th>
                      <th className="py-2 pr-4 text-gray-400">Provider</th>
                      <th className="py-2 pr-4 text-gray-400">Purpose</th>
                      <th className="py-2 text-gray-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-white/5">
                      <td className="py-2 pr-4 font-mono text-xs">_fbp</td>
                      <td className="py-2 pr-4">Facebook</td>
                      <td className="py-2 pr-4">Ad targeting</td>
                      <td className="py-2">3 months</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-xs">_gcl_au</td>
                      <td className="py-2 pr-4">Google Ads</td>
                      <td className="py-2 pr-4">Conversion tracking</td>
                      <td className="py-2">3 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Similar Technologies */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Similar Technologies</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>In addition to cookies, we may use similar technologies:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li><strong>Local Storage:</strong> Data stored in your browser that persists after you close it</li>
              <li><strong>Session Storage:</strong> Temporary data cleared when you close your browser</li>
              <li><strong>Pixels/Web Beacons:</strong> Small graphics that track page views and email opens</li>
              <li><strong>Device Fingerprinting:</strong> Collecting device characteristics for identification</li>
            </ul>
          </div>
        </section>

        {/* Your Choices */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Your Cookie Choices</h2>
          
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">Cookie Consent Banner</h3>
              <p className="text-gray-400">
                When you first visit our platform, you'll see a cookie consent banner where you can accept 
                or reject non-essential cookies. You can change your preferences at any time.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">Browser Settings</h3>
              <p className="text-gray-400">
                Most browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-400 mt-2">
                <li>Block all cookies</li>
                <li>Accept only first-party cookies</li>
                <li>Delete cookies when you close your browser</li>
                <li>Browse in private/incognito mode</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">Opt-Out Links</h3>
              <div className="space-y-2 text-gray-400">
                <p>You can opt out of specific tracking services:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Google Analytics Opt-out</a></li>
                  <li><a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Facebook Ad Preferences</a></li>
                  <li><a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Network Advertising Initiative Opt-out</a></li>
                </ul>
              </div>
            </div>

            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-yellow-300">
                <strong>Note:</strong> Disabling cookies may affect the functionality of our Services. Essential 
                cookies cannot be disabled as they are necessary for the platform to work.
              </p>
            </div>
          </div>
        </section>

        {/* Third-Party Cookies */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Third-Party Cookies</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              Some cookies are placed by third parties on our behalf. These third parties may collect 
              information about your online activities over time and across different websites.
            </p>
            <p className="text-gray-400">
              We work with trusted partners including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Google (Analytics, Ads)</li>
              <li>Facebook/Meta (Social plugins, Ads)</li>
              <li>Payment processors (Stripe, PayFast)</li>
              <li>Customer support tools</li>
            </ul>
            <p className="text-gray-400">
              These third parties have their own privacy policies governing how they use your information.
            </p>
          </div>
        </section>

        {/* Updates */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Changes to This Policy</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our practices or 
              applicable laws. We will notify you of material changes by posting the updated policy with 
              a new "Last Updated" date.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>If you have questions about our use of cookies, please contact us:</p>
            <div className="p-6 bg-white/5 rounded-xl border border-white/10 space-y-3">
              <p><strong>Metro City Trading (Pty) Ltd</strong></p>
              <p className="text-gray-400">Email: <a href="mailto:privacy@metrocitygroove.online" className="text-purple-400 hover:underline">privacy@metrocitygroove.online</a></p>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="mt-12 p-6 bg-white/5 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="text-purple-400 hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-purple-400 hover:underline">Terms of Service</Link>
            <Link to="/gdpr" className="text-purple-400 hover:underline">GDPR Compliance</Link>
          </div>
        </section>
      </main>
    </div>;
};
export default CookiePolicy;